// Copyright 2022 Sorama B.V.

import { autoinject } from "aurelia-framework";
import { ProgressIndicator } from "libraries/ProgressIndicator/ProgressIndicator";
import { Api } from "libraries/WebApi/Api";
import { IWebRequestHandler } from "libraries/WebRequestHandler/IWebRequestHandler";
import { Repository } from "repositories/Repository";
import { CamIV64DeviceType } from "services/Hardware/CamHTTP/CamIV64Helpers";

@autoinject
export class FirmwareRepository extends Repository {
    constructor(private readonly webRequestHandler: IWebRequestHandler) {
        super();
    }

    public getVersions(deviceType: CamIV64DeviceType): Promise<string[]> {
        return this.webRequestHandler
            .createGetRequest<string[]>(Api.firmware + "/" + this.containerName(deviceType) + "/Versions")
            .send()
            .then((x) => x.content);
    }

    public getLatestVersion(deviceType: CamIV64DeviceType): Promise<string> {
        return this.webRequestHandler
            .createGetRequest<string>(Api.firmware + "/" + this.containerName(deviceType) + "/LatestVersion")
            .send()
            .then((x) => x.content);
    }

    public downloadLatestFirmware(
        deviceType: CamIV64DeviceType,
        callback: (progress: ProgressIndicator) => void
    ): Promise<Blob> {
        return this.webRequestHandler
            .createGetRequest<Blob>(Api.firmware + "/" + this.containerName(deviceType) + "/DownloadLatest")
            .withDownloadProgress(callback)
            .withBinaryResponse()
            .send()
            .then((x) => x.content);
    }

    public getFirmwareSize(deviceType: CamIV64DeviceType, version: string): Promise<number> {
        return this.webRequestHandler
            .createGetRequest<number>(
                Api.firmware + "/" + this.containerName(deviceType) + "/Size" + "?version=" + version
            )
            .send()
            .then((x) => x.content);
    }

    public downloadFirmware(
        deviceType: CamIV64DeviceType,
        version: string,
        callback: (progress: ProgressIndicator) => void
    ): Promise<Blob> {
        return this.webRequestHandler
            .createGetRequest<Blob>(
                Api.firmware + "/" + this.containerName(deviceType) + "/Download" + "?version=" + version
            )
            .withDownloadProgress(callback)
            .withBinaryResponse()
            .send()
            .then((x) => x.content);
    }

    /**
     * Maps device type to firmware storage container name
     * @param deviceType The CamIV64 device type
     * @returns The corresponding firmware storage container name
     */
    public containerName(deviceType: CamIV64DeviceType): string {
        // Explicitly map device types to container names
        const containerNames: Record<CamIV64DeviceType, string> = {
            iV64: "iv64",
            iV64s: "iv64s",
            iV64Ex: "iv64ex",
        };

        if (deviceType in containerNames) {
            return containerNames[deviceType];
        } else {
            throw new Error(`Unsupported device type: ${deviceType}`);
        }
    }
}
